import React from "react";
import { Link } from "react-router-dom";
import LegalPage, {
  ContactLink,
  LegalSection,
  OPERATOR,
} from "../components/LegalPage";

function Terms() {
  return (
    <LegalPage title="Terms of service">
      <p>
        These terms are an agreement between you and {OPERATOR} ("we" or "us"),
        who runs Plateo. They cover the Plateo website and the Plateo Chrome
        extension (together, "Plateo"). By creating an account or using Plateo,
        you agree to these terms and to our{" "}
        <Link to="/privacy">privacy policy</Link>, which explains how we handle
        your information. If you don't agree, please don't use Plateo.
      </p>

      <LegalSection title="1. Who can use Plateo">
        <ul className="mb-0">
          <li>
            You must be at least 16. If you're under 18, you need a parent or
            guardian's permission.
          </li>
          <li>
            Give accurate details when you sign up, keep your password private,
            and tell us if you think someone else is using your account.
          </li>
          <li>
            One person per account. You're responsible for what happens in your
            account.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="2. What Plateo does">
        <p>
          Plateo helps you find jobs, keep track of them, write cover letters,
          tailor your resume, find people at a company and fill in job
          applications. It can show public LinkedIn profiles a search engine has
          already indexed (name, headline and profile link only -{" "}
          <strong>Plateo does not provide people's email addresses</strong>),
          and, only if you connect your Gmail, send outreach from your own
          address. Platypus, if you turn it on, searches and prepares
          applications on the schedule you set; it never submits anything. Some
          features use AI and other services that cost us money, so they're
          available by request and come with limits (see section 4).
        </p>
        <p className="mb-0">
          We're still building Plateo. Features may change, be limited or be
          removed, and Plateo may sometimes be unavailable. We'll try to give
          notice before big changes.
        </p>
      </LegalSection>

      <LegalSection title="3. You're in charge of your applications">
        <ul className="mb-0">
          <li>
            <strong>Check everything before you submit it.</strong> AI-written
            text, autofilled answers and job details Plateo reads from a page
            can be wrong or incomplete. You're responsible for everything you
            send to an employer, including making sure it's true.
          </li>
          <li>
            Plateo never submits an application for you. You review it and press
            submit yourself, on the employer's own site, under that site's
            rules.
          </li>
          <li>
            Plateo doesn't promise interviews, offers or any result, and isn't
            connected to the employers or job boards it shows. Job listings come
            from third parties, so check them yourself before sharing personal
            information.
          </li>
          <li>
            Plateo doesn't give legal, immigration or career advice. Visa
            sponsorship information is a guide, not a guarantee.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Access, AI credits and limits">
        <ul className="mb-0">
          <li>
            Tracking jobs is free and we don't currently charge money or take
            payment for anything. AI features (resume tailoring, cover letters,
            outreach emails, AI answers and Platypus) are available by
            request, and we decide who gets access.
          </li>
          <li>
            Access comes with a number of AI credits. When you run out, those
            features pause until you ask for more and we approve it.
          </li>
          <li>
            Credits have no cash value, can't be transferred and may expire. We
            can change limits or remove access at any time.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="5. Acceptable use">
        <p>Don't use Plateo to:</p>
        <ul className="mb-0">
          <li>apply with false information or pretend to be someone else;</li>
          <li>
            send spam or unwanted messages to recruiters or anyone else, or
            break anti-spam, privacy or other laws when you contact someone
            (including through a connected Gmail account - those messages are
            yours, sent from your address);
          </li>
          <li>
            harass anyone, or use people-finder results for anything other than
            reaching out about a job you're applying to;
          </li>
          <li>
            break the law or the rules of the job sites, LinkedIn, Google and
            employers you use Plateo with;
          </li>
          <li>
            get around limits, rate limits or security measures, share your
            account or extension connection, or use it on behalf of others;
          </li>
          <li>
            scrape, copy, resell or overload Plateo, or try to break into it or
            other users' data;
          </li>
          <li>
            upload anything you don't have the right to share, or anything
            harmful or malicious.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="6. Your content">
        <p>
          Your resumes, profile, cover letters, answers and other material stay
          yours. You give us permission to store, process and copy them only as
          needed to run Plateo for you, including sending parts of them to the
          services listed in our privacy policy (for example, to write a cover
          letter). This permission ends when you delete the content or your
          account, apart from routine backups that are overwritten over time.
        </p>
        <p className="mb-0">
          Text Plateo writes for you, like cover letters and answers, is yours
          to use. Similar text may be written for other people.
        </p>
      </LegalSection>

      <LegalSection title="7. Our content">
        <p className="mb-0">
          Plateo itself (its software, design, name and logo) belongs to us. You
          can use it under these terms, but you can't copy, modify or
          redistribute it. If you send us feedback or ideas, we can use them
          without owing you anything.
        </p>
      </LegalSection>

      <LegalSection title="8. Other services">
        <p className="mb-0">
          Plateo relies on other services listed in the privacy policy, such as
          OpenAI for AI writing, Serper for the people finder, public job boards
          and the a16z Speedrun jobs API for listings, and Google if you connect
          Gmail, and it links to
          LinkedIn and employer sites. Search results and job listings come from
          those third parties, can be wrong or out of date, and those people
          have not endorsed or contacted you. We aren't responsible for those
          services or sites, and your use of them follows their own terms. If
          you connect Gmail, you also agree to Google's terms for that
          connection: Plateo can only send as you (never read your inbox), you
          are responsible for every message sent from your address, and you can
          disconnect in your Profile or revoke Plateo in your Google account at
          any time.
        </p>
      </LegalSection>

      <LegalSection title="9. The Chrome extension">
        <p className="mb-0">
          The extension works only when you ask it to, as described in the
          privacy policy. Websites change often, so it may fill a field
          incorrectly or miss one. Always review the form - including anything
          the extension filled in - before submitting. You can disconnect or
          uninstall it at any time.
        </p>
      </LegalSection>

      <LegalSection title="10. Ending your account">
        <p className="mb-0">
          You can delete your account at any time from your Profile page. We can
          suspend or close an account, or remove access to AI features, if you
          break these terms, if your use puts Plateo or other people at risk, or
          if we stop offering Plateo. Where it's reasonable, we'll tell you
          first. Sections 3, 6, 7 and 11 to 13 still apply after an account is
          closed.
        </p>
      </LegalSection>

      <LegalSection title="11. No warranties">
        <p className="mb-0">
          Plateo is provided "as is" and "as available". As far as the law
          allows, we don't make any promises about it, including that it will be
          accurate, reliable, uninterrupted or fit for a particular purpose, or
          that it will help you get a job.
        </p>
      </LegalSection>

      <LegalSection title="12. Limit on our liability">
        <p>
          As far as the law allows, we aren't liable for indirect, incidental,
          special or consequential losses, or for lost opportunities, income or
          data, arising from your use of Plateo. That includes mistakes in
          applications, AI-written text or job information. Our total liability
          for any claim about Plateo is limited to the greater of what you paid
          us in the 12 months before the claim or $50.
        </p>
        <p className="mb-0">
          You agree to cover our reasonable costs if a claim is brought against
          us because you broke these terms or the law while using Plateo.
        </p>
      </LegalSection>

      <LegalSection title="13. Law and disputes">
        <p className="mb-0">
          These terms are governed by the laws of the State of New York, USA,
          without regard to its conflict-of-law rules. Any dispute will be
          handled in the state or federal courts located in New York, and you
          and we agree to those courts' jurisdiction. Before going to court,
          please contact us so we can try to sort it out.
        </p>
      </LegalSection>

      <LegalSection title="14. Changes to these terms">
        <p className="mb-0">
          We may update these terms as Plateo changes. We'll change the date at
          the top, and for important changes we'll tell you in the app or by
          email before they take effect. If you keep using Plateo after that,
          you accept the new terms. If you don't, you can delete your account.
        </p>
      </LegalSection>

      <LegalSection title="15. General">
        <p className="mb-0">
          These terms and the privacy policy are the whole agreement between you
          and us about Plateo. If part of them can't be enforced, the rest still
          applies. Not enforcing a part right away doesn't mean we give it up.
          You can't transfer these terms to someone else; we can transfer them
          if Plateo changes hands.
        </p>
      </LegalSection>

      <LegalSection title="Contact">
        <p className="mb-0">
          Questions about these terms? Contact us at <ContactLink />.
        </p>
      </LegalSection>
    </LegalPage>
  );
}

export default Terms;
